import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { GhostRiskItem } from '@/hooks/useRevenueIntelligence';
import { readAllRowsKeyset } from '@/lib/supabase-pagination';
import { dedupeByPerson } from '@/lib/crm/needs-attention';
import { formatUiWaitingDuration } from '@/lib/ui-format';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

export interface CampaignPulse {
  organization_id: string;
  heyreach_campaign_id: string;
  campaign_name: string;
  archived_at: string | null;
  campaign_key: string | null;
  goal: string | null;
  methodology: string | null;
  product_line: string | null;
  total_leads: number;
  total_drafts: number;
  pending_drafts: number;
  sent_count: number;
  approved_count: number;
  rejected_count: number;
  leads_contacted: number;
  last_activity: string | null;
  first_activity: string | null;
  response_rate: number | null;
  edit_rate: number | null;
}

// os_campaign_stats is a security_invoker view over os_linkedin_drafts, whose
// RLS is USER-SEAT-scoped (migration 015: members see only their own
// drafts) — so pulse data is USER-dependent, not just organization-dependent.
// The authenticated user id must be part of the cache key or two members of
// the same organization can read/overwrite each other's cached rows
// (r3-F1 same-org cross-seat cache leak).
export const campaignPulseQueryKey = (
  userId: string | null,
  organizationId: string | null,
): readonly ['campaign-pulse', string | null, string | null] =>
  ['campaign-pulse', userId, organizationId];

/**
 * Whether a campaign row counts as archived. A row missing `archived_at`
 * entirely (rather than carrying an explicit `null`) is treated as ACTIVE —
 * fail open to visible/operational, not to hidden — so a stale cache entry
 * or fixture predating this column can never silently vanish from the
 * default view (F4).
 */
export const isArchivedCampaign = (
  campaign: { archived_at?: string | null },
): boolean => Boolean(campaign.archived_at);

export interface ActivityItem {
  id: string;
  leadName: string;
  leadCompany: string | null;
  campaignId: string;
  eventType: 'reply_received' | 'draft_approved' | 'message_sent' | 'draft_rejected' | 'new_lead';
  timestamp: string;
  status: string;
  methodology: string;
}

export interface NeedsAttentionItem {
  id: string;
  type: 'ghost_risk' | 'stale_draft' | 'failed_draft';
  leadName: string;
  leadCompany: string | null;
  campaignId: string;
  reason: string;
  urgency: 'high' | 'medium' | 'low';
  deepLink: string;
  timestamp: string;
}

export const getGhostRiskDeepLink = (item: GhostRiskItem): string => {
  const contactId = item.contactId?.trim();
  return contactId
    ? `/crm/contact/${encodeURIComponent(contactId)}`
    : '/ai-prodavac';
};

// ─────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────

export const useCampaignPulse = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<CampaignPulse[]>({
    queryKey: campaignPulseQueryKey(userId, organizationId),
    queryFn: async () => {
      if (!organizationId) return [];

      // os_campaign_stats is security_invoker, so RLS normally scopes rows
      // to the caller's organization on its own — but RLS visibility can
      // briefly exceed the session's context org during a membership
      // transition (e.g. a user being added to org B while still active in
      // org A). Without this explicit filter, a foreign-org row picked up
      // during that window would land in org A's cache (keyed only by
      // user+org, not by row identity), and this hook's rows later feed an
      // archive write. Filtering here is defense-in-depth, not a
      // replacement for RLS (F1).
      //
      // r7-F6: paginated via readAllRowsKeyset. os_campaign_stats is GROUPed by
      // (organization_id, heyreach_campaign_id), so this is bounded by
      // CAMPAIGN count, not lead/draft count — but campaign count is NOT
      // schema-enforced, archiving never deletes a campaign's row (it only
      // flips archived_at), and Mission Control derives its active/archived
      // counts AND lifetime totals (leads contacted, messages sent) straight
      // from this array (mission-control.tsx) by summing every row it gets
      // back. An unpaginated read would silently truncate at PostgREST's
      // 1,000-row default page cap the instant an org's campaign count ever
      // crosses it, making those counts quietly wrong rather than merely
      // hiding a UI row. heyreach_campaign_id is the unique, immutable cursor;
      // mutable last_activity never controls a page boundary.
      const campaigns = await readAllRowsKeyset<CampaignPulse, string>(
        () =>
          supabase
            .from('os_campaign_stats')
            .select('*')
            .eq('organization_id', organizationId),
        'heyreach_campaign_id',
        (campaign) => campaign.heyreach_campaign_id,
        (error) => {
          throw error;
        },
      );

      // Display order is restored here, where no page boundary depends on it.
      return campaigns.sort((left, right) => {
        if (left.last_activity !== right.last_activity) {
          if (left.last_activity === null) return 1;
          if (right.last_activity === null) return -1;

          const byActivity = right.last_activity.localeCompare(left.last_activity);
          if (byActivity !== 0) return byActivity;
        }
        return left.heyreach_campaign_id.localeCompare(right.heyreach_campaign_id);
      });
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};

// os_linkedin_drafts is org-scoped (organization_id, migration 001) AND
// seat-scoped (user_id + the member policy in migration 015), so the visible
// draft set is a function of (user, organization) — same reasoning as
// campaignPulseQueryKey above, one level lower on the same table.
export const useActivityStream = (limit = 20) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ActivityItem[]>({
    queryKey: ['activity-stream', userId, organizationId, limit],
    queryFn: async () => {
      if (!organizationId) return [];

      // Explicit organization predicate on top of RLS, for the same
      // membership-transition window described in useCampaignPulse (F1).
      // Bounded by .limit(limit) — this is a page of UI (the activity feed),
      // not a correctness tally, so it does not need readAllRows.
      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select('id, lead_name, lead_company, heyreach_campaign_id, status, updated_at, incoming_at, methodology_used, sent_at, approved_at')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((draft) => {
        let eventType: ActivityItem['eventType'] = 'reply_received';
        let timestamp = draft.updated_at;

        if (draft.status === 'sent' && draft.sent_at) {
          eventType = 'message_sent';
          timestamp = draft.sent_at;
        } else if (draft.status === 'approved' && draft.approved_at) {
          eventType = 'draft_approved';
          timestamp = draft.approved_at;
        } else if (draft.status === 'rejected') {
          eventType = 'draft_rejected';
        } else {
          eventType = 'reply_received';
          timestamp = draft.incoming_at;
        }

        return {
          id: draft.id,
          leadName: draft.lead_name,
          leadCompany: draft.lead_company,
          campaignId: draft.heyreach_campaign_id,
          eventType,
          timestamp,
          status: draft.status,
          methodology: draft.methodology_used,
        };
      });
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 15000,
  });
};

export const useNeedsAttention = (ghostRiskItems: GhostRiskItem[] = []) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  const staleDraftsQuery = useQuery<NeedsAttentionItem[]>({
    queryKey: ['stale-drafts', userId, organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();

      // Explicit organization predicate on top of RLS (F1), same as above.
      // Bounded by .limit(10) — the Needs Attention list is a fixed-size UI
      // queue, not a tally, so readAllRows would only cost round trips.
      //
      // The inner join to os_conversation_threads is the DECLUTTER (2026-08-12).
      // This read used to look at os_linkedin_drafts alone, so a draft stayed
      // "needs attention" forever no matter what happened to the conversation it
      // belonged to. Measured on tacit-prod that day: 443 drafts sat in
      // pending_approval, every one of them older than four hours, the oldest
      // from 29 January — and 158 of them belonged to threads that were already
      // RESOLVED. Since the list shows the ten OLDEST, it was showing almost
      // nothing but closed conversations from months ago, which is what made the
      // whole surface read as noise.
      //
      // `!inner` makes the thread filter actually filter: without it PostgREST
      // returns the draft row with a null embed rather than dropping it, and the
      // status predicate below would silently do nothing.
      const { data, error } = await supabase
        .from('os_linkedin_drafts')
        .select(
          'id, lead_name, lead_company, heyreach_campaign_id, heyreach_lead_id, thread_id, created_at, status, ' +
            'os_conversation_threads!inner(status, deleted_at)'
        )
        .eq('organization_id', organizationId)
        .eq('status', 'pending_approval')
        // A draft that a newer inbound already replaced is not work. This is the SAME predicate the
        // Reply Queue enforces (useReplyQueue.ts) and that is_actionable_pending_draft() defines in
        // SQL — `is` rather than `eq` because is_superseded is NULL on older rows and NULL means
        // "not superseded". Its absence is why Marina Mijatović appeared three times in a five-item
        // list on 12.08.2026: three superseded drafts on one conversation, all still listed.
        .not('is_superseded', 'is', true)
        .lt('created_at', fourHoursAgo)
        .not('os_conversation_threads.status', 'in', '("resolved")')
        .is('os_conversation_threads.deleted_at', null)
        .order('created_at', { ascending: true })
        // Over-fetch, then collapse to one row per person and take ten. Limiting to 10 in SQL and
        // deduping afterwards would return fewer than ten people whenever anyone holds several live
        // drafts — the list would quietly shrink instead of showing more work.
        .limit(60);

      if (error) throw error;

      // Same shape-narrowing the draft campaign domain uses (useReplyQueue.ts):
      // src/types/database.ts has not been regenerated since migration 038, so it
      // does not know the drafts→threads relationship and infers the embedded
      // select as GenericStringError. The columns below are the ones this query
      // literally asks for; when the generated types are refreshed this narrowing
      // becomes redundant and should be deleted rather than kept.
      const staleDrafts = (data ?? []) as unknown as Array<{
        id: string;
        lead_name: string;
        lead_company: string | null;
        heyreach_campaign_id: string;
        heyreach_lead_id: string | null;
        thread_id: string | null;
        created_at: string;
      }>;

      // One row per PERSON, oldest wait first, then the ten the surface shows. See
      // src/lib/crm/needs-attention.ts for why the identity key is thread → lead id → name.
      const oneRowPerPerson = dedupeByPerson(
        staleDrafts.map((d) => ({
          id: d.id,
          leadName: d.lead_name,
          threadId: d.thread_id,
          leadId: d.heyreach_lead_id,
          timestamp: d.created_at,
          row: d,
        }))
      ).slice(0, 10);

      return oneRowPerPerson.map(({ row: draft }): NeedsAttentionItem => {
        const hoursWaiting = Math.round(
          (Date.now() - new Date(draft.created_at).getTime()) / 3600000
        );
        return {
          id: draft.id,
          type: 'stale_draft',
          leadName: draft.lead_name,
          leadCompany: draft.lead_company,
          campaignId: draft.heyreach_campaign_id,
          reason: `Waiting ${formatUiWaitingDuration(draft.created_at)} for approval`,
          urgency: hoursWaiting > 12 ? 'high' : 'medium',
          // Link to the PERSON, not to a filter. This used to be the constant
          // `/inbox?status=pending_approval` for every item, so clicking any name
          // in Needs Attention landed you on a generic list you then had to search
          // by hand — the row already knew exactly who it meant. Mirrors
          // leadDossierHref (reply-queue-panel.tsx): /leads/:heyreachLeadId with the
          // draft and thread ids carried so the dossier opens on THIS exchange.
          // Falls back to the old filter only when the lead id is genuinely absent,
          // which is a broken row rather than a normal one.
          deepLink: draft.heyreach_lead_id
            ? `/leads/${encodeURIComponent(draft.heyreach_lead_id)}?${new URLSearchParams({
                draft: draft.id,
                thread: draft.thread_id ?? '',
              }).toString()}`
            : `/inbox?status=pending_approval`,
          timestamp: draft.created_at,
        };
      });
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
  });

  // Combine ghost risk items + stale drafts
  const ghostAttentionItems: NeedsAttentionItem[] = ghostRiskItems
    .filter((item) => item.ghostProbability >= 0.6)
    .map((item): NeedsAttentionItem => ({
      id: item.draftId,
      type: 'ghost_risk',
      leadName: item.leadName,
      leadCompany: item.leadCompany,
      campaignId: '',
      reason: `${item.daysInactive}d inactive - ${item.recommendedAction}`,
      urgency: item.ghostProbability >= 0.8 ? 'high' : 'medium',
      deepLink: getGhostRiskDeepLink(item),
      timestamp: new Date(Date.now() - item.daysInactive * 86400000).toISOString(),
    }));

  const allItems = [
    ...ghostAttentionItems,
    ...(staleDraftsQuery.data || []),
  ].sort((a, b) => {
    const urgencyOrder = { high: 0, medium: 1, low: 2 };
    return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
  });

  return {
    items: allItems,
    isLoading: staleDraftsQuery.isLoading,
    isError: staleDraftsQuery.isError,
    error: staleDraftsQuery.error,
  };
};
